import { css } from "@emotion/css";
import { Callout, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { GitCommitMethods } from "@octopusdeploy/legacy-action-properties";
import type { EnvironmentV2Resource, PersistenceSettings, ProcessTemplateParameterResource, ProcessType } from "@octopusdeploy/octopus-server-client";
import { ControlType, Permission, unbindParameterName } from "@octopusdeploy/octopus-server-client";
import { difference, keyBy, union } from "lodash";
import * as React from "react";
import { ProducerTemplatesParameterSelector } from "~/areas/platformHub/processEditor/ProducerTemplatesParameterSelector";
import { isVersionControlledProcess } from "~/areas/projects/components/Process/Common/CommonProcessHelpers";
import { isProcessOrProjectTemplateProcessType } from "~/areas/projects/components/Process/types";
import { environmentV2ChipList } from "~/components/Chips/index";
import FeatureToggleVisibility from "~/components/FeatureToggle/New/FeatureToggleVisibility";
import { EnvironmentV2MultiSelect } from "~/components/MultiSelect/EnvironmentV2MultiSelect";
import type { SummaryNode } from "~/components/form/Sections/ExpandableFormSection";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import Summary from "~/components/form/Sections/Summary";
import { Note } from "~/components/form/index";
interface ArgoCDCommitMethodProps {
    environments: EnvironmentV2Resource[] | undefined;
    persistenceSettings: PersistenceSettings | undefined;
    commitMethod: string;
    updateCommitMethod: (s: string) => void;
    processType: ProcessType | undefined;
    environmentsValue: string;
    updateEnvironmentsValue: (s: string | undefined) => void;
    environmentsTemplateParameter: string;
    updateEnvironmentsTemplateParameterValue: (s: string | undefined) => void;
    commitMethodError: string;
    expandedByDefault: boolean;
    onParameterAdded?: (parameters: ProcessTemplateParameterResource[]) => void;
}
export function ArgoCDGitCommitMethodFormSection(props: ArgoCDCommitMethodProps) {
    function gitCommitMethodSummary(): SummaryNode {
        const gitCommitMethod = props.commitMethod ?? GitCommitMethods.DirectCommit;
        switch (gitCommitMethod) {
            case GitCommitMethods.DirectCommit:
                return Summary.summary("Direct commit for all environments");
            case GitCommitMethods.PullRequest:
                return Summary.summary("Create pull requests for all environments");
            default: {
                if (isProcessOrProjectTemplateProcessType(props.processType)) {
                    return Summary.summary(<span>
                            Create pull requests for environments specified by the parameter <strong>{unbindParameterName(props.environmentsTemplateParameter)}</strong>
                        </span>);
                }
                else {
                    return Summary.summary(<span>Create pull requests for {getChipsForEnvironments(getPullRequestEnvironments())} </span>);
                }
            }
        }
    }
    function getChipsForEnvironments(environments: string[]) {
        if (environments.length === 0) {
            return <em>please select environments</em>;
        }
        return environmentV2ChipList(props.environments ?? [], environments);
    }
    function getPullRequestEnvironments(): string[] {
        if (StringHelper.isNullOrWhiteSpace(props.environmentsValue)) {
            return [];
        }
        return props.environmentsValue.split(",");
    }
    function shouldBlockUserFromRemovingUnavailableEnvironments() {
        return !isVersionControlled() && hasUnavailableEnvironments();
    }
    function onPullRequestSpecificEnvironmentsChanged(environments: string[]): void {
        //Prevent user from removing environments they don't have access to
        if (shouldBlockUserFromRemovingUnavailableEnvironments()) {
            props.updateEnvironmentsValue(union(environments, getUnavailableEnvironments()).join(","));
        }
        else {
            props.updateEnvironmentsValue(environments.join(","));
        }
    }
    function onPullRequestSpecificEnvironmentsTemplateParameterChanged(environments: string | null): void {
        props.updateEnvironmentsTemplateParameterValue(environments ?? undefined);
    }
    function isVersionControlled(): boolean {
        return props.persistenceSettings != undefined && props.processType != undefined && isVersionControlledProcess(props.persistenceSettings, props.processType);
    }
    function getUnavailableEnvironments(): string[] {
        const environmentsByKey = keyBy(props.environments, "Id");
        const knownEnvironments = Object.keys(environmentsByKey);
        return difference(getPullRequestEnvironments(), knownEnvironments);
    }
    function hasUnavailableEnvironments(): boolean {
        const unavailableEnvironments = getUnavailableEnvironments();
        return unavailableEnvironments.length > 0;
    }
    function updateCommitMethod(commitMethod: string) {
        //Clear out the envrionment list if it's not applicable
        if (commitMethod !== GitCommitMethods.PerEnvironment) {
            if (isProcessOrProjectTemplateProcessType(props.processType)) {
                props.updateEnvironmentsTemplateParameterValue(undefined);
            }
            else {
                props.updateEnvironmentsValue(undefined);
            }
        }
        props.updateCommitMethod(commitMethod);
    }
    return (<FeatureToggleVisibility toggle={"argocd-create-pull-request"}>
            <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.CommitMethod" title="Git Commit Method" summary={gitCommitMethodSummary()} help={"Select the environments where Octopus should open pull requests or commit directly."} isExpandedByDefault={props.expandedByDefault}>
                <RadioButtonGroup value={props.commitMethod ?? GitCommitMethods.DirectCommit} onChange={(val) => updateCommitMethod(val)} error={props.commitMethodError}>
                    <RadioButton value={GitCommitMethods.DirectCommit} label="Direct commits for all environments" disabled={shouldBlockUserFromRemovingUnavailableEnvironments()}/>
                    <RadioButton value={GitCommitMethods.PullRequest} label="Pull requests for all environments" disabled={shouldBlockUserFromRemovingUnavailableEnvironments()}/>
                    <RadioButton value={GitCommitMethods.PerEnvironment} label="Pull requests for specific environments" disabled={shouldBlockUserFromRemovingUnavailableEnvironments()}/>
                    {(props.commitMethod ?? GitCommitMethods.DirectCommit) === GitCommitMethods.PerEnvironment && (<>
                            {isProcessOrProjectTemplateProcessType(props.processType) ? (<div className={styles.environmentsParameterStyles}>
                                    <ProducerTemplatesParameterSelector onParameterChange={(p) => onPullRequestSpecificEnvironmentsTemplateParameterChanged(p)} selectedParameter={props.environmentsTemplateParameter} controlType={ControlType.Environments} allowClear={true} onParameterAdded={props.onParameterAdded}/>
                                </div>) : (<EnvironmentV2MultiSelect environments={props.environments ?? []} value={getPullRequestEnvironments()} onChange={(environments) => onPullRequestSpecificEnvironmentsChanged(environments)}/>)}
                            <Note>Octopus will commit changes directly for all unselected environments.</Note>
                            {shouldBlockUserFromRemovingUnavailableEnvironments() && (<Callout type={"information"} title={"Insufficient permissions"}>
                                    The {Permission.EnvironmentView} permission is preventing the commit method from being changed because you do not have access to all environments.
                                </Callout>)}
                        </>)}
                </RadioButtonGroup>
            </ExpandableFormSection>
        </FeatureToggleVisibility>);
}
const styles = {
    environmentsParameterStyles: css({
        marginTop: space[12],
    }),
};
